import { createSlice, PayloadAction, UnknownAction } from '@reduxjs/toolkit'

import history from '@/config/router/history'
import { PATHS } from '@/config/router/routes'
import { startAppListening } from '@/config/store/listenerMiddleware'
import { DENIED_STORE_STATUSES } from '@/constants/common'
import { sendLoginDeniedEvent } from '@/ducks/app/helpers'
// Removed circular dependency - selectors will be accessed directly from state
import { startInitialization } from '@/ducks/app/slices/app.shared'
import { selectToken } from '@/ducks/auth/auth.selectors'
import { createNotificationSound } from '@/ducks/orders/utils/orders.utils'
import storeApi from '@/ducks/store/api/store.api'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'
import {
  setLoginDeniedStatus,
  setStoreProfileData
} from '@/ducks/store/slice/store.slice'
import {
  ApiGetStoreAvailability,
  IStoreProfileStatuses
} from '@/ducks/store/store.types'
import {
  readFromLocalStorage,
  setToLocalStorage
} from '@/services/localStorage'

const DEFAULT_APP_SETTINGS = {
  confirmMenuHideAction: true
}

export interface AppState {
  isInitialized: boolean
  isSidebarOpen: boolean
  settings: {
    confirmMenuHideAction: boolean
  }
  flags: Record<string, any>
  notificationAppId: string | null
  notificationSound: HTMLAudioElement
  pendingOrderIds: string[]
  hasUserInteracted: boolean
}

const initialState: AppState = {
  isInitialized: false,
  isSidebarOpen: false,
  settings: { ...DEFAULT_APP_SETTINGS, ...readFromLocalStorage('settings') },
  flags: {},
  notificationAppId: null,
  notificationSound: createNotificationSound(),
  pendingOrderIds: [],
  hasUserInteracted: false
}

const SLICE_NAME = 'app'

export const appSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    initializeApp: state => {
      state.isInitialized = true
    },
    updateSettings: (
      state,
      action: PayloadAction<Partial<AppState['settings']>>
    ) => {
      state.settings = { ...state.settings, ...action.payload }
    },
    setSideBarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload
    },
    updateFlags: (state, action: PayloadAction<Partial<AppState['flags']>>) => {
      state.flags = action.payload
    },
    setNotificationAppId: (
      state,
      action: PayloadAction<{ id: string | null }>
    ) => {
      state.notificationAppId = action.payload.id
    },
    stopAudioNotification: state => {
      state.notificationSound?.pause()

      if (state.notificationSound?.currentTime) {
        state.notificationSound.currentTime = 0
      }
    },
    playAudioNotification: state => {
      state.notificationSound?.play()
    },
    addOrderIdToPending: (
      state,
      action: PayloadAction<{ orderId: string }>
    ) => {
      state.pendingOrderIds.push(action.payload.orderId)
    },
    removeOrderIdFromPending: (
      state,
      action: PayloadAction<{ orderId: string }>
    ) => {
      state.pendingOrderIds = state.pendingOrderIds.filter(
        orderId => orderId !== action.payload.orderId
      )
    },
    setUserInteracted: state => {
      state.hasUserInteracted = true
    }
  }
})

startAppListening({
  predicate: (_: UnknownAction, currentState, previousState) => {
    const currentPendingOrders = currentState.app?.pendingOrderIds || []
    const previousPendingOrders = previousState.app?.pendingOrderIds || []
    return (
      currentPendingOrders.length === 0 &&
      previousPendingOrders.length > 0
    )
  },
  effect: async function stopNotificationSound(_: UnknownAction, listenerApi) {
    listenerApi.dispatch(stopAudioNotification())
  }
})

startAppListening({
  predicate: (_: UnknownAction, currentState, previousState) => {
    const currentSettings = currentState.app?.settings
    const previousSettings = previousState.app?.settings
    return currentSettings !== previousSettings
  },
  effect: async function syncSettingsToLocalStorage(
    _: UnknownAction,
    listenerApi
  ) {
    const state = listenerApi.getState()
    const settings = state.app?.settings
    if (settings) {
      setToLocalStorage({ settings })
    }
  }
})

startAppListening({
  actionCreator: startInitialization,
  effect: async (_, listenerApi) => {
    const token = selectToken(listenerApi.getState())
    const storeId = selectStoreId(listenerApi.getState())

    try {
      if (!token || !storeId) throw new Error('No token or id')

      const storeInfo = await listenerApi
        .dispatch(
          storeApi.endpoints.fetchStoreProfile.initiate({
            store_id: storeId
          })
        )
        .unwrap()

      const storeStatus = storeInfo?.profileData
        ?.status as IStoreProfileStatuses
      listenerApi.dispatch(setLoginDeniedStatus({ status: storeStatus }))

      if (DENIED_STORE_STATUSES.includes(storeStatus)) {
        sendLoginDeniedEvent(storeStatus, storeId)

        history.push(PATHS.LOGIN_DENIED)
        return
      }

      const { storeName, avatar, phoneNumber, phoneCode } = {
        avatar: storeInfo.profileData.store_img_profile,
        storeName: storeInfo.profileData.store_name,
        phoneNumber: storeInfo.profileData.phone?.phone1?.number,
        phoneCode: storeInfo.profileData.phone?.phone1?.country_code
      }

      const { availability }: ApiGetStoreAvailability = await listenerApi
        .dispatch(
          storeApi.endpoints.fetchStoreAvailability.initiate({
            store_id: storeId
          })
        )
        .unwrap()

      listenerApi.dispatch(
        setStoreProfileData({
          availability,
          storeName,
          avatar,
          phoneNumber,
          phoneCode
        })
      )

      if ([PATHS.LOGIN].includes(history.location.pathname)) {
        history.push(PATHS.ORDERS)
      }
      // eslint-disable-next-line no-empty
    } catch (error) {
    } finally {
      listenerApi.dispatch(initializeApp())
    }
  }
})

export const {
  updateFlags,
  initializeApp,
  updateSettings,
  setSideBarOpen,
  setNotificationAppId,
  playAudioNotification,
  stopAudioNotification,
  addOrderIdToPending,
  removeOrderIdFromPending,
  setUserInteracted
} = appSlice.actions
export default appSlice.reducer
